// TechnicianPayCard — Phase 10 R3
//
// OWNER / MANAGER ONLY. Defense-in-depth wraps existing manager-only RLS on
// the `technician_compensation` table. TECHNICIAN role cannot view or edit
// any compensation in R3 — neither their own nor any other technician's.
//
// Surface lives inside the existing TechnicianForm "General" tab and is
// gated by both the `labor_engine_enabled` entitlement and the
// `ui_technician_pay` exposure flag.

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2, DollarSign, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import type { TechPayMode } from '@/lib/labor/computeJobTechPay';

interface Props {
  technicianId: string;
}

export function TechnicianPayCard({ technicianId }: Props) {
  const { activeShop, user } = useAuth();
  const { isManagerView } = useUserRole();
  const { shopTimezone } = useShopTimezone();
  const queryClient = useQueryClient();

  const laborEnabled = useFeatureFlag('labor_engine_enabled');
  const techPayEnabled = useExposureFlag('ui_technician_pay');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payMode, setPayMode] = useState<TechPayMode>('hourly');
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [flatRate, setFlatRate] = useState<string>('');

  const shopId = activeShop?.id;
  const visible = laborEnabled && techPayEnabled && isManagerView;

  useEffect(() => {
    if (!visible || !shopId || !technicianId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await (supabase as any)
        .from('technician_compensation')
        .select('pay_mode, hourly_rate, flat_rate_per_hour')
        .eq('shop_id', shopId)
        .eq('technician_id', technicianId)
        .is('effective_to', null)
        .order('effective_from', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (data) {
        setPayMode((data.pay_mode as TechPayMode) ?? 'hourly');
        setHourlyRate(
          data.hourly_rate != null ? String(Number(data.hourly_rate)) : '',
        );
        setFlatRate(
          data.flat_rate_per_hour != null
            ? String(Number(data.flat_rate_per_hour))
            : '',
        );
      } else {
        setPayMode('hourly');
        setHourlyRate('');
        setFlatRate('');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, shopId, technicianId]);

  // Hidden entirely for non-managers / when flags are off.
  if (!visible) return null;

  const handleSave = async () => {
    if (!shopId) return;
    const hourlyNum = parseFloat(hourlyRate);
    const flatNum = parseFloat(flatRate);

    if (payMode === 'hourly' && (!isFinite(hourlyNum) || hourlyNum <= 0)) {
      toast({
        title: 'Hourly rate required',
        description: 'Enter a positive hourly rate.',
        variant: 'destructive',
      });
      return;
    }
    if (payMode === 'flat_rate' && (!isFinite(flatNum) || flatNum <= 0)) {
      toast({
        title: 'Flat rate required',
        description: 'Enter a positive flat-rate per hour.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const effectiveFrom = getTodayInShopTz(shopTimezone);

      // Close any open row.
      await (supabase as any)
        .from('technician_compensation')
        .update({ effective_to: effectiveFrom })
        .eq('shop_id', shopId)
        .eq('technician_id', technicianId)
        .is('effective_to', null);

      // Insert new effective row. hourly_rate is required (NOT NULL on existing table)
      // so we always carry a non-zero hourly value (default to 0 only when missing).
      const { error } = await (supabase as any)
        .from('technician_compensation')
        .insert({
          shop_id: shopId,
          technician_id: technicianId,
          pay_mode: payMode,
          hourly_rate: isFinite(hourlyNum) && hourlyNum > 0 ? hourlyNum : 0,
          flat_rate_per_hour:
            payMode === 'flat_rate' && isFinite(flatNum) && flatNum > 0
              ? flatNum
              : null,
          effective_from: effectiveFrom,
          effective_to: null,
          created_by: user?.id ?? null,
        });
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['tech-pay-config', shopId, technicianId] });
      queryClient.invalidateQueries({ queryKey: ['tech-compensation', technicianId] });
      queryClient.invalidateQueries({ queryKey: ['shop-compensation-rates', shopId] });

      toast({
        title: 'Pay settings saved',
        description: 'Future pay computations will use the new rate.',
      });
    } catch (e: any) {
      toast({
        title: 'Failed to save pay settings',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Technician Pay
        </CardTitle>
        <CardDescription>
          Pay configuration is OWNER/MANAGER only. Per-job pay is computed for
          flat-rate technicians; hourly is recorded but per-job pay is deferred.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pay-mode">Pay mode</Label>
                <Select
                  value={payMode}
                  onValueChange={(v) => setPayMode(v as TechPayMode)}
                >
                  <SelectTrigger id="pay-mode">
                    <SelectValue placeholder="Pay mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="flat_rate">Flat rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="hourly-rate">Hourly rate ($/hr)</Label>
                <Input
                  id="hourly-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="0.00"
                />
                <p className="text-[11px] text-muted-foreground">
                  {payMode === 'hourly'
                    ? 'Used for hourly mode (per-job pay deferred in R3).'
                    : 'Recorded for reference; not used in flat-rate mode.'}
                </p>
              </div>

              {payMode === 'flat_rate' && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="flat-rate">Flat rate ($ per flag-hour)</Label>
                  <Input
                    id="flat-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={flatRate}
                    onChange={(e) => setFlatRate(e.target.value)}
                    placeholder="0.00"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Per flag-hour. Pay time = max(reference, sold).
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Save pay settings
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
